import { Severity, SpecHeaderType } from "node-library/dist/constants";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanySampleSpecificationHeaders,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecificationDirectory,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { ButtonTextLoadingAnimation } from "../animation/ButtonLoadingAnimation";
import { GreenButton } from "../button";
import { CustomDropdown, CustomTextInput } from "../inputs";
import { RedErrorIcon } from "../toast/errorIcons";
import { useSnackbar } from "../toast/showToastMessage";
import { ModalWithButtons } from "./modal";
import { GreenTickIcon } from "../toast/successIcons";
import { overwriteFrontendCompanyInfo } from "../../helpers/companyHelper";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
}

export default function AddSpecColumnModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { open, setOpen, company, setCompany } = props;

	const [newColumnName, setNewColumnName] = useState<string>("");
	const [newColumnType, setNewColumnType] = useState<SpecHeaderType>(
		SpecHeaderType.SHORT_TEXT,
	); // Default to TEXT or your enum default

	const [loading, setLoading] = useState<boolean>(false);
	useEffect(() => {
		if (open) {
			setNewColumnName("");
		}
	}, [open]);

	const handleAddColumn = async () => {
		if (!newColumnName.trim()) {
			openSnackbar(
				translation("ColumnNameRequired"),
				Severity.ERROR,
				undefined,
				<RedErrorIcon />,
			);
			return;
		}

		setLoading(true);
		try {
			// Calculate the next display order
			const maxOrder = company.companySpecHeaders.reduce(
				(max, h) => (h.displayOrder > max ? h.displayOrder : max),
				0,
			);

			const payload = {
				headerName: newColumnName,
				headerType: newColumnType,
				displayOrder: maxOrder + 1,
				// companyId is handled by backend 'additionalInfo', so usually not needed in body if extracted from token/session
				// otherwise add it here: companyId: company.companyId
			};

			// Replace '/specifications/create-header' with your actual API route
			const response = await sendPOSTRequest(
				"/spec-readings/create-specification-spec-header-v1",
				payload,
			);

			if (response.success) {
				openSnackbar(
					translation("ColumnCreated"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />,
				);
				const newHeaderId: string = response.data.newHeaderId;
				const newColumn: FrontendCompanySampleSpecificationHeaders = {
					headerId: newHeaderId,
					name: newColumnName,
					type: newColumnType,
					displayOrder: maxOrder + 1,
				};
				overwriteFrontendCompanyInfo(
					{
						...company,
						companySpecHeaders: [...company.companySpecHeaders, newColumn],
					},
					setCompany,
				);
				setOpen(false);
			}
		} catch (error) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	};

	const addSpecColumnModalContent: React.ReactNode = (
		<div className="registration-input-content">
			<CustomTextInput
				id={"spec-column-name"}
				value={newColumnName}
				setValue={setNewColumnName}
				label={translation("ColumnName")}
				type={"text"}
				required
			></CustomTextInput>
			<CustomDropdown
				id={"spec-type-name"}
				label={translation("Type")}
				options={Object.values(SpecHeaderType).map((value) => {
					return { value: value, title: translation(value) };
				})}
				value={newColumnType}
				setValue={setNewColumnType}
			></CustomDropdown>
		</div>
	);
	const addSpecFolderModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("AddColumn")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-create-folder-button"
					id="submit-create-folder-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("Create")
						)
					}
					onClick={handleAddColumn}
					disabled={loading}
				></GreenButton>,
			]}
		>
			{addSpecColumnModalContent}
		</ModalWithButtons>
	);
	return addSpecFolderModal;
}
